// Timeline data, independent of where the files come from: steps, their changes and entity ids.
import { diffModels, emptyModel, renameHints, type DiscoveryResult, type MigrationEntry, type ModelDiff, type ModelSpec } from '@modelviewer/core';
import type { ModelRef } from '../state/workspace';
import type { OperationsResult } from '../workers/parse.worker';
import { entityIds } from './lineage';

export interface TimelineStep {
  /** Migration id, or `snapshot` for a snapshot that differs from the last migration. */
  id: string;
  name: string;
  timestamp: string | null;
  migration: MigrationEntry | null;
  ref: ModelRef;
  /** The model after this step; null when the migration has no Designer file. */
  model: ModelSpec | null;
  /** Designer or snapshot file. */
  file: string | null;
  operations: OperationsResult | null;
  /** Changes since the previous step that has a model. */
  diff: ModelDiff | null;
  previous: number | null;
  error: string | null;
  /** The snapshot differs from the model of the last migration. */
  drift: boolean;
}

export interface Timeline {
  setKey: string;
  steps: TimelineStep[];
  /** Per step: entity key to a diagram id that survives renames. */
  ids: Map<string, string>[];
  /** The folder contents it was built from; a newer discovery means it is out of date. */
  builtFor: DiscoveryResult | null;
}

export type MigrationStepInput = Pick<TimelineStep, 'id' | 'name' | 'timestamp' | 'migration' | 'ref' | 'model' | 'file' | 'operations' | 'error'>;

export interface SnapshotInput {
  ref: ModelRef;
  model: ModelSpec;
  file: string;
}

const nextFrame = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/**
 * Links migrations into a timeline: each model is compared with the previous known one, using the
 * migration's rename operations as hints. A snapshot that differs from the last migration's model
 * becomes a final step marked as drift.
 */
export async function assembleTimeline(setKey: string, migrations: MigrationStepInput[], snapshot: SnapshotInput | null, builtFor: DiscoveryResult | null): Promise<Timeline> {
  const steps: TimelineStep[] = migrations.map((m) => ({ ...m, diff: null, previous: null, drift: false }));
  let previous: number | null = null;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    if (!step.model) continue;
    const before = previous === null ? emptyModel() : steps[previous]!.model!;
    step.diff = diffModels(before, step.model, { hints: step.operations ? renameHints(step.operations.up) : [] });
    step.previous = previous;
    previous = i;
    if (i % 8 === 7) await nextFrame();
  }
  if (snapshot) {
    const last = previous === null ? null : steps[previous]!.model!;
    const diff = diffModels(last ?? emptyModel(), snapshot.model);
    if (last === null || diff.changes.length > 0) {
      steps.push({
        id: 'snapshot',
        name: last ? 'Model snapshot' : 'Current model',
        timestamp: null,
        migration: null,
        ref: snapshot.ref,
        model: snapshot.model,
        file: snapshot.file,
        operations: null,
        diff,
        previous,
        error: null,
        drift: last !== null,
      });
    }
  }
  return { setKey, steps, ids: entityIds(steps), builtFor };
}

/** Index of the step showing a model reference: the migration, or the last step for the current model. */
export function stepIndexOf(timeline: Timeline, ref: ModelRef): number {
  if (ref.migrationId === null) {
    for (let i = timeline.steps.length - 1; i >= 0; i--) if (timeline.steps[i]!.model) return i;
    return timeline.steps.length - 1;
  }
  return timeline.steps.findIndex((s) => s.id === ref.migrationId);
}

/** Number of logical changes a step made (0 when its model is unknown). */
export const changeCount = (step: TimelineStep) => step.diff?.changes.length ?? 0;
